// Token (◈) wallet, rush pricing and sinks — MONETIZATION.md is authoritative.
// M1: client-side only. Earned tokens are spent first; premium balance is a display cache (server later).
import { ERA_BY_ID } from '../data/eras.js';
import { emit } from '../core/bus.js';

export const STARTER_GRANT = 30;
export const DAILY_LOOP = [2, 2, 3, 3, 4, 4, 10];
export const LEDGER_CAP = 200;

export const PACKS = [
  { id: 'handful', name: 'Handful', usd: 2.99, tokens: 300, bonusPct: 0 },
  { id: 'crate', name: 'Crate', usd: 4.99, tokens: 520, bonusPct: 4 },
  { id: 'barrel', name: 'Barrel', usd: 9.99, tokens: 1100, bonusPct: 10 },
  { id: 'tanker', name: 'Tanker', usd: 19.99, tokens: 2400, bonusPct: 20 },
  { id: 'supertanker', name: 'Supertanker', usd: 49.99, tokens: 6500, bonusPct: 30 },
  { id: 'galactic', name: 'Galactic', usd: 99.99, tokens: 14000, bonusPct: 40 },
  { id: 'founder', name: "Founder's Bundle", usd: 9.99, tokens: 1000, oneTime: true, extras: ['gilded_derrick', 'auto_queue_badge'] },
];

const eraNum = (state) => ERA_BY_ID[state.eraId]?.num ?? 1;

// SKU table. cost(state, ctx) -> tokens. `k` entries are rush constants used by rushCost.
export const TOKEN_SINKS = {
  rush_research: { name: 'Rush research project', k: 2.0 },
  rush_megaproject: { name: 'Rush megaproject', k: 2.5 },
  rush_rigup: { name: 'Rush rig-up / rig move', k: 0.8 },
  rush_drill: { name: 'Drill to target now (skips risk rolls)', k: 1.2 },
  rush_casing: { name: 'Rush casing / completion / capping / fishing', k: 1.0 },
  rush_shipment: { name: 'Rush shipment / delivery', k: 0.6 },
  eureka: { name: 'Eureka! (−25% remaining research)', cost: (s) => 10 + 3 * eraNum(s) },
  reroll_setback: { name: 'Reroll a research setback', cost: (s) => 8 + 2 * eraNum(s) },
  extra_lab_slot: { name: 'Extra lab slot (max +1)', cost: () => 500 },
  emergency_hire: { name: 'Emergency crew hire (skill ≥ 60)', cost: (s) => 5 + 2 * eraNum(s) },
  well_insurance: { name: 'Well-control insurance (per well)', cost: (s) => 3 + 2 * eraNum(s) },
  expert_team: { name: 'Blowout expert team', cost: (s) => 5 + eraNum(s) },
  barbecue: { name: 'Crew barbecue (+30 morale)', cost: () => 3 },
  reveal_plot: { name: 'Premium survey: one plot exact', cost: (s) => 8 + 2 * eraNum(s) },
  reveal_field: { name: 'Premium survey: whole field', cost: (s) => 60 + 15 * eraNum(s) },
  lloyds_policy: { name: "Lloyd's political-risk policy (1 yr)", cost: (s, ctx) => 20 + 10 * (ctx?.riskTier ?? 0) },
  intelligence: { name: 'Intelligence report (90 days)', cost: () => 15 },
  emergency_extraction: { name: 'Emergency extraction', cost: (s) => 25 + 5 * eraNum(s) },
  mercenaries: { name: 'Mercenary guards (+0.3 guard factor, 90 d)', cost: () => 10 },
};

export function newWallet() {
  return {
    earned: STARTER_GRANT, premiumCached: 0, premiumSyncedAt: null, accountId: null, pendingSpends: [],
    daily: { lastClaimDate: null, streak: 0 },
    lifetime: { earned: STARTER_GRANT, spent: 0, purchased: 0 },
    ledger: [{ day: null, delta: STARTER_GRANT, reason: 'starter', ref: null }],
    insurance: {}, cosmetics: [],
  };
}

function wallet(state) {
  state.tokens ??= newWallet();
  return state.tokens;
}

export function balance(state) {
  const w = wallet(state);
  return (w.earned ?? 0) + (w.premiumCached ?? 0);
}

function pushLedger(state, delta, reason, ref) {
  const w = wallet(state);
  w.ledger.push({ day: state.day ?? null, delta, reason, ref: ref ?? null });
  if (w.ledger.length > LEDGER_CAP) w.ledger.splice(0, w.ledger.length - LEDGER_CAP);
}

export function grant(state, amount, reason = 'grant', ref = null) {
  const n = Math.round(amount);
  if (!(n > 0)) return { ok: false, msg: 'Nothing to grant.' };
  const w = wallet(state);
  w.earned += n;
  w.lifetime.earned += n;
  pushLedger(state, n, reason, ref);
  try { emit('tokens', { day: state.day, delta: n, reason, balance: balance(state), text: `+${n} ◈ (${reason})` }); } catch { /* ignore */ }
  return { ok: true, msg: `+${n} ◈` };
}

// Spend tokens: earned first, then premium cache (server-authoritative later).
export function spend(state, amount, reason, ref = null) {
  const n = Math.ceil(amount);
  if (!(n > 0)) return { ok: false, msg: 'Invalid amount.' };
  const w = wallet(state);
  if (balance(state) < n) return { ok: false, msg: `Need ${n} ◈ (you have ${balance(state)}).` };
  const fromEarned = Math.min(w.earned, n);
  w.earned -= fromEarned;
  w.premiumCached -= n - fromEarned;
  w.lifetime.spent += n;
  pushLedger(state, -n, reason, ref);
  try { emit('tokens', { day: state.day, delta: -n, reason, balance: balance(state) }); } catch { /* ignore */ }
  return { ok: true, msg: `Spent ${n} ◈.` };
}

// Remaining days for pricing. Fixed timers use daysLeft; work timers estimate remaining/rate.
export function remainingDays(state, timer) {
  if (!timer) return 0;
  if (timer.work && timer.work.remaining > 0) return Math.max(1, Math.ceil(timer.work.remaining / Math.max(timer.work.rate ?? 1, 1e-6)));
  return Math.max(0, timer.daysLeft ?? 0);
}

export function rushPrice(k, days, half = false) {
  if (days <= 0) return 0;
  const full = Math.max(1, Math.ceil(k * Math.pow(days, 0.75)));
  return half ? Math.max(1, Math.ceil(0.6 * full)) : full;
}

export function rushCost(state, timerId, { half = false } = {}) {
  const t = typeof timerId === 'object' ? timerId : state.timers?.[timerId];
  if (!t || t.rushable === false) return null;
  return rushPrice(t.rushK ?? 1, remainingDays(state, t), half);
}

export function sinkCost(state, sku, ctx) {
  const s = TOKEN_SINKS[sku];
  return s?.cost ? s.cost(state, ctx) : null;
}

// Local calendar date YYYY-MM-DD (M1: device date; server date later).
export function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function claimDaily(state, today = localDate()) {
  const w = wallet(state);
  if (w.daily.lastClaimDate === today) return { ok: false, msg: 'Already claimed today. Come back tomorrow!' };
  const prev = w.daily.lastClaimDate ? new Date(`${w.daily.lastClaimDate}T00:00:00`) : null;
  const now = new Date(`${today}T00:00:00`);
  const gap = prev ? Math.round((now - prev) / 86400000) : null;
  w.daily.streak = gap === 1 ? w.daily.streak + 1 : 1;
  w.daily.lastClaimDate = today;
  const amt = DAILY_LOOP[(w.daily.streak - 1) % DAILY_LOOP.length];
  grant(state, amt, 'daily', `day${((w.daily.streak - 1) % 7) + 1}`);
  return { ok: true, msg: `Daily claim: +${amt} ◈ (day ${((w.daily.streak - 1) % 7) + 1} of 7).`, amount: amt };
}
